import React, { useEffect, useMemo, useState } from "react";
import { CreatePageList } from "../../components/Layouts/PageList";
import { FormProvider, useForm } from "react-hook-form";
import {
  AnalysisPhasesTritio,
  ConditionPhases,
  ConditionPhasesTypes,
  IFormConditioning,
  InitialPhases,
  KeysTabsAnalysis,
} from "../../types/samples.types";
import { Box } from "@mui/material";
import { useSample } from "./utils/useSample";
import ConditioningForm from "./components/conditioningForm";
import PhasesInitial from "./components/initialComponents/phases";
import useTabs from "./utils/useTab";
import {
  AnalysisType,
  processingTablesDisabledTrue,
  TYPE_METHOD_IG_GT,
} from "./utils/constants";
import { mapObjectToIds } from "../../utils/helpers";
import PhasesAnalysis from "./components/analysisComponents/phases";
import useAnalysis from "./utils/useAnalysis";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicSidebar } from "../../context/DynamicSidebarContext";
import {
  convertProcessing,
  parseDate,
  parseDateTime,
  samplingMapKeys,
  validateFields,
} from "./utils/helpers";
import PrintLabel from "./components/initialComponents/printLabel";
import { Loading, MButton } from "@app/components";
import { CommentsButtons } from "@app/components/Actions";
import { usePrintTag } from "./utils/usePrintTag";
import MessageDialog from "@app/components/Login/MessageDialog";
import { errorAlert, successAlert } from "@app/utils/alerts";
import { useTabsPreparation } from "./components/analysisComponents/tritio/tritioVapor/preparation/utils/useTabPreparation";
import {
  KeysInitialCode,
  setsInitialPhases,
} from "./config/phases/InitialPhases";
import {
  configFunctionMap,
  AnalysisWhitMeasurement,
  AnalysisWithFiles,
  AnalysisNeedUpdateCounting,
} from "./config/phases/AnalysisPhases";
import { useDetector, useEquipment, useIsotopos } from "@services/servicesGenericsTables";
import { mapDataToIsotopes } from "./components/analysisComponents/IG/helpers";
import usePermission from "@app/context/PermissionContext";
import { buildEquipmentPayload } from "./utils/equipmentHelpers";
import { PU_AM_CM_ANALYSIS_TYPES } from "./utils/calculationConstants";

const ConditioningSample = () => {
  const {
    saveInitial,
    loadingSave,
    get,
    stateSample,
    saveProcessingInitial,
    deleteProcessing,
    updateProcessing,
  } = useSample();
  const { setTabValue: setTabValueTritioVapor } = useTabsPreparation();
  const { equipmentList } = useEquipment();
  const { detectorsList } = useDetector();
  const { isAdmin, isSuperAdmin } = usePermission();

  const { addDynamicSidebar } = useDynamicSidebar();
  const {
    getCurrentIds,
    saveProcessing,
    saveProcessingWhitMeasure,
    saveFileITIG,
    updateCoutingITIG,
    updateCalculationById,
    saveIsotopeResultCalculation,
    currentCalculation,
    fetchIsotopeResultCalculationByID,
    deleteIsotopeResultCalculationByID,
  } = useAnalysis();
  const ids = getCurrentIds();
  const { idMuestra } = useParams();
  const { tabValue, tabValueAnalysis, phaseSelected, setPhaseSelected } =
    useTabs();

  const { isotoposList } = useIsotopos();

  const [dialogData, setDialogData] = useState<any>({});
  const [loadingIsotope, setLoadingIsotope] = useState<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    open: false,
    message: "",
    type: "edit",
  });
  const [dataToSave, setDataToSave] = useState<any>(null);
  const { fetchTag, loadingTag } = usePrintTag();
  const navigate = useNavigate();

  useEffect(() => {
    const sample = get(Number(idMuestra));
    if (!sample) {
      addDynamicSidebar("info", undefined);
      errorAlert("No se encontró la muestra.");
      navigate("/portal/muestras");
      return;
    }
    addDynamicSidebar("info", sample);
  }, [idMuestra]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      addDynamicSidebar("menu", undefined);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm<IFormConditioning>({
    mode: "all",
  });

  const isInitial = phaseSelected === ConditionPhases.Initial;
  const isAnalysis = phaseSelected === ConditionPhases.Analysis;

  const checkIsEditAnalysis = (phase: string) => {
    if (phase === AnalysisType.PREPARATION && ids.preparationId) return true;
    if (phase === AnalysisType.MEASUREMENT && ids.measurementId) return true;
    if (phase === AnalysisType.COUNTING && ids.countingId) return true;
    if (phase === AnalysisType.CALCULATION && ids.calculationId) return true;
    return false;
  };

  const getModalMessage = (set: string) => {
    const currentPhase = setsInitialPhases.find((phase) => phase.set === set);
    const currentSample = get(Number(idMuestra));

    if (!currentPhase) return "Fase no encontrada.";
    const nextSet = currentPhase.check;

    if (nextSet === ConditionPhases.Analysis && currentSample?.processings)
      return `Va a modificar el registro y existen procesos posteriores.`;

    if (stateSample[nextSet]?.isSave) {
      return `Va a modificar el registro y existen procesos posteriores.`;
    }
    return "Se va a modificar el registro.";
  };

  const submit = async (data: IFormConditioning) => {
    const mapeaPayload = mapObjectToIds(data);

    if (isInitial) {
      const set = setsInitialPhases[tabValue].set;
      const isNew = !stateSample[set]?.isSave;
      const hasPermison = isAdmin || isSuperAdmin;
      if (isNew && !hasPermison) {
        errorAlert("No tiene permisos para generar el registro.");
        return;
      }

      const endpoint = setsInitialPhases[tabValue].endpoint;
      if (KeysInitialCode.processing === set) {
        const processingMapDataTable = convertProcessing(data);

        const processingCategory = mapeaPayload.processing_category;
        if (!processingCategory) return;
        const mappeaPayloadWithCategoryAcc: any = [];

        const preloadIds =
          stateSample.processing.current &&
          stateSample.processing.current.length > 0
            ? stateSample.processing.current.map((item) => ({
                processingId: item.id,
                id: item.processing_category?.id,
                name: item.processing_category?.name,
              }))
            : [];

        for (const category of processingCategory) {
          const mappeaPayloadWithCategory =
            processingTablesDisabledTrue[category](mapeaPayload);

          const updatedData = {
            ...mappeaPayloadWithCategory,
            id:
              preloadIds.find((item) => {
                return (
                  item?.id?.toString() &&
                  mappeaPayloadWithCategory.processing_category?.toString() &&
                  mappeaPayloadWithCategory.processing_category.toString() ===
                    item?.id?.toString()
                );
              })?.processingId || undefined,
          };
          mappeaPayloadWithCategoryAcc.push(updatedData);
        }

        const allCategoryIds = JSON.parse(
          JSON.stringify(mappeaPayloadWithCategoryAcc)
        )
          .flatMap((item) => item.processing_category)
          .map((id) => parseInt(id));

        const missingProcessingIds =
          JSON.parse(JSON.stringify(preloadIds))
            .filter((item) => !allCategoryIds.includes(item.id))
            .map((item) => item.processingId) || [];

        if (stateSample[set]?.isSave) {
          const message = getModalMessage(set);
          setDataToSave({
            data: mappeaPayloadWithCategoryAcc,
            endpoint,
            missingProcessingIds,
            processingMapDataTable,
          });
          setOpenConfirmModal({
            open: true,
            message,
            type: "edit",
          });
        } else {
          await deleteProcessing(missingProcessingIds);
          const resp = await saveProcessingInitial(
            mappeaPayloadWithCategoryAcc,
            endpoint,
            processingMapDataTable
          );
          await updateProcessing(Number(idMuestra));

          if (resp) successAlert("Se ha guardado correctamente");
        }
      } else {
        const payload = setsInitialPhases[tabValue].functionMap(mapeaPayload);
        if (stateSample[set]?.isSave) {
          const message = getModalMessage(set);
          setDataToSave({ data: payload, endpoint });
          setOpenConfirmModal({
            open: true,
            message,
            type: "edit",
          });
        } else {
          await saveInitial(payload, endpoint);
        }
      }
    }

    if (isAnalysis) {
      if (
        tabValueAnalysis === AnalysisPhasesTritio.PREPARATION &&
        data?.typeAnalysis?.codigo === KeysTabsAnalysis.H3_V
      ) {
        const validationErrors = validateFields(data, setTabValueTritioVapor);

        if (validationErrors.length > 0) {
          errorAlert(validationErrors[0]);
          return;
        }
      }

      const isPreparationWhitMeasurement = AnalysisWhitMeasurement.includes(
        data?.typeAnalysis?.codigo as KeysTabsAnalysis
      );
      const hasFile = AnalysisWithFiles.includes(
        data?.typeAnalysis?.codigo as KeysTabsAnalysis
      );

      const needUpdateCounting = AnalysisNeedUpdateCounting.includes(
        data?.typeAnalysis?.codigo as KeysTabsAnalysis
      );
      const mapeaPayloadWithIds = { ...mapeaPayload, ...ids };
      const configAnalysis =
        configFunctionMap[data?.typeAnalysis?.codigo || ""];

      if (!configAnalysis || !configAnalysis[tabValueAnalysis]) {
        errorAlert("Configuración de análisis no encontrada para el tipo de análisis seleccionado.");
        return;
      }

      const endpoint = configAnalysis[tabValueAnalysis].endpoint;
      const payload =
        configAnalysis[tabValueAnalysis].functionMap(mapeaPayloadWithIds);

      const isEdit = checkIsEditAnalysis(endpoint);
      const hasPermison = isAdmin || isSuperAdmin;

      if (!isEdit && !hasPermison) {
        errorAlert("No tiene permisos para generar el registro.");
        return;
      }

      if (isEdit) {
        setDataToSave({
          data: payload,
          endpoint,
          isPreparationWhitMeasurement,
          hasFile,
          needUpdateCounting,
          typeAnalysis: data?.typeAnalysis?.codigo,
        });
        setOpenConfirmModal({
          open: true,
          message: "Se va a modificar el registro.",
          type: "edit",
        });
      } else {
        const resp = isPreparationWhitMeasurement
          ? await saveProcessingWhitMeasure(payload, endpoint)
          : await saveProcessing(payload, endpoint);

        if (hasFile && endpoint === AnalysisType.CALCULATION) {
          setDataToSave({
            data: payload,
            endpoint,
            isPreparationWhitMeasurement,
            hasFile,
            needUpdateCounting,
            typeAnalysis: data?.typeAnalysis?.codigo,
          });
          await handleConfirmFile({ ...payload, id: resp });
          setOpenConfirmModal({
            open: needUpdateCounting,
            message:
              "¿Desea modificar los registros del contaje con los datos del archivo?",
            type: "editCouting",
          });
        }
      }
    }
  };

  const handleConfirmFile = async (payload) => {
    const hasFileOne = !!payload.source_app_calculation_info_file;
    const hasFileTwo = !!payload.source_app_calculation_info_file_2;
    if (hasFileOne) {
      await saveFileITIG(
        payload.source_app_calculation_info_file,
        "source_app_calculation_info_file"
      );
      const isGenie =
        payload.source_app_calculation_info ===
        TYPE_METHOD_IG_GT.GENIE.toUpperCase();
      if (isGenie) {
        setLoadingIsotope(true);
        const isotopeResults = mapDataToIsotopes(
          payload.dataIsotopes,
          isotoposList,
          payload.id || currentCalculation?.id
        )?.filter((item) => item.isotope !== null);

        const fetchIsotopeResultCalculation =
          await fetchIsotopeResultCalculationByID();
        if (fetchIsotopeResultCalculation.length > 0) {
          for (const isotopeResult of fetchIsotopeResultCalculation) {
            await deleteIsotopeResultCalculationByID(isotopeResult.id);
          }
        }
        for (const isotopeResult of isotopeResults) {
          const success = await saveIsotopeResultCalculation(isotopeResult);
          if (!success) {
            console.error(`Error al guardar el isotopo:`, isotopeResult);
          }
        }
        setLoadingIsotope(false);
      }
    }
    if (hasFileTwo)
      await saveFileITIG(
        payload.source_app_calculation_info_file_2,
        "source_app_calculation_info_file_2"
      );

    await updateCalculationById();
  };

  const handleConfirmFileWithChangeCounting = async () => {
    const { data, typeAnalysis } = dataToSave;
    const isGenie = data.source_app_calculation_info
      ? data.source_app_calculation_info.toUpperCase() ===
        TYPE_METHOD_IG_GT.GENIE.toUpperCase()
      : false;

    const isPuAmCm = PU_AM_CM_ANALYSIS_TYPES.includes(typeAnalysis as KeysTabsAnalysis);

    // Construir payload base
    let payload: any = {
      ...data,
      detector: undefined,
      first_counting_time: isGenie
        ? data.calculation_time_count
        : data?.total_sample_counting_time || undefined,
      equipment: undefined,
      counting_date: isGenie
        ? parseDateTime(data.calculation_date_countig_file)
        : parseDate(
            data.calculation_date_countig_file_exe ||
              form.getValues("calculation_date_countig_file_exe")
          ),
    };

    // Usar el helper para construir el payload de equipos
    const equipmentPayload = buildEquipmentPayload(
      data,
      typeAnalysis as KeysTabsAnalysis,
      equipmentList,
      detectorsList,
      form.getValues
    );

    payload = {
      ...payload,
      ...equipmentPayload,
    };

    await updateCoutingITIG(payload);

    setOpenConfirmModal({
      open: false,
      message: "",
      type: "edit",
    });
  };

  const handleConfirmSave = async () => {
    setOpenConfirmModal({
      open: false,
      message: "",
      type: "edit",
    });
    if (dataToSave) {
      const {
        data,
        endpoint,
        isPreparationWhitMeasurement,
        missingProcessingIds,
        processingMapDataTable,
        hasFile,
        needUpdateCounting,
      } = dataToSave;
      if (isInitial) {
        if (KeysInitialCode.processing === endpoint) {
          await deleteProcessing(missingProcessingIds);

          const resp = await saveProcessingInitial(
            data,
            endpoint,
            processingMapDataTable
          );
          await updateProcessing(Number(idMuestra));

          if (resp) successAlert("Se ha guardado correctamente");
        } else await saveInitial(data, endpoint);
      } else if (isAnalysis) {
        isPreparationWhitMeasurement
          ? await saveProcessingWhitMeasure(data, endpoint)
          : await saveProcessing(data, endpoint);

        if (hasFile && endpoint === AnalysisType.CALCULATION) {
          await handleConfirmFile(data);
          setOpenConfirmModal({
            open: needUpdateCounting,
            message:
              "¿Desea modificar los registros del contaje con los datos del archivo?",
            type: "editCouting",
          });
        }
      }
    }
  };

  const handleBack = () => {
    window.location.assign("/portal/muestras");
  };

  const clearForm = () => {
    form.reset();
    handleBack();
  };

  const changePhase = (phase: ConditionPhasesTypes) => setPhaseSelected(phase);

  const watchedFields = form.watch(samplingMapKeys as any);

  const isInitialAndSampling = useMemo(() => {
    //const samplingData = stateSample["sampling"];
    return (
      tabValue === InitialPhases.Sampling && isInitial /*&&
      samplingData?.id &&
      isEqualSampling(samplingData.current, form.getValues())*/
    );
  }, [stateSample, watchedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => setDialogData({ ...dialogData, active: false });

  const handlePrintLabel = async () => {
    setDialogData({
      component: "Confirm",
      title: "¿Qué etiqueta desea imprimir?",
      status: "no",
      active: true,
      handleClose: handleClose,
      handleConfirm: async (typeTag) => {
        await fetchTag(Number(idMuestra), typeTag.value);
        setDialogData({ ...dialogData, active: false });
      },
    });
  };

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submit)}>
          <CreatePageList
            title={""}
            footer={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                {isInitialAndSampling && (
                  <MButton
                    variant="contained"
                    color="info"
                    sx={{ mr: 2 }}
                    text="Imprimir etiqueta"
                    onClick={handlePrintLabel}
                  />
                )}
                <MButton
                  variant="contained"
                  color="primary"
                  text="Guardar"
                  type="submit"
                />
                <MButton
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 2 }}
                  text="Cancelar"
                  onClick={clearForm}
                />
              </Box>
            }
          >
            <ConditioningForm
              changePhase={changePhase}
              phaseSelected={phaseSelected}
            />
            {isInitial && <PhasesInitial />}
            {isAnalysis && <PhasesAnalysis />}
          </CreatePageList>
        </form>
        {(loadingSave || loadingTag || loadingIsotope) && <Loading />}
        {isInitialAndSampling && (
          <PrintLabel handleClose={handleClose} dialogData={dialogData} />
        )}
      </FormProvider>
      <CommentsButtons idSample={Number(idMuestra)} />

      <MessageDialog
        data={{
          component: "Confirm",
          title: openConfirmModal.message,
          message: "¿Desea guardar los cambios?",
          status: "confirm",
          active: openConfirmModal.open,
          handleConfirm:
            openConfirmModal.type === "edit"
              ? handleConfirmSave
              : handleConfirmFileWithChangeCounting,
        }}
        handleClose={() =>
          setOpenConfirmModal({
            open: false,
            message: "",
            type: "edit",
          })
        }
        handleConfirm={
          openConfirmModal.type === "edit"
            ? handleConfirmSave
            : handleConfirmFileWithChangeCounting
        }
      ></MessageDialog>
    </>
  );
};

export default ConditioningSample;
